/**
 * Google Identity Services + LinkedIn sign-in helpers.
 *
 * Google: the GSI script renders the official button and hands back an
 * ID token (credential) that the backend verifies.
 *
 * LinkedIn: authorization-code hop — `state` is kept in this origin's
 * sessionStorage and validated by the callback page before the code is
 * sent to the backend (LinkedIn's client secret never leaves the server).
 */

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID ?? '';
const LINKEDIN_CLIENT_ID = import.meta.env.VITE_LINKEDIN_CLIENT_ID ?? '';
const LINKEDIN_REDIRECT_URI =
  import.meta.env.VITE_LINKEDIN_REDIRECT_URI ?? `${window.location.origin}/auth/callback`;

// hl=en pins the rendered button to English regardless of browser locale.
const GSI_SCRIPT_SRC = 'https://accounts.google.com/gsi/client?hl=en';
const LINKEDIN_AUTHORIZATION_URL = 'https://www.linkedin.com/oauth/v2/authorization';
const LINKEDIN_SCOPES = 'openid profile email';
const LINKEDIN_STATE_KEY = 'adgptise_linkedin_oauth_state';
const LINKEDIN_SILENT_AUTH_KEY = 'adgptise_linkedin_silent_auth';

export interface GoogleIdConfiguration {
  client_id: string;
  callback: (response: { credential?: string }) => void;
}

export interface GsiButtonConfiguration {
  theme?: 'outline' | 'filled_blue' | 'filled_black';
  size?: 'large' | 'medium' | 'small';
  text?: 'continue_with' | 'signin_with' | 'signup_with' | 'signin' | 'signup';
  shape?: 'rectangular' | 'pill' | 'circle' | 'square';
  logo_alignment?: 'left' | 'center';
  width?: number;
}

interface GoogleAccountsId {
  initialize(config: GoogleIdConfiguration): void;
  renderButton(element: HTMLElement, options: GsiButtonConfiguration): void;
}

declare global {
  interface Window {
    google?: { accounts?: { id?: GoogleAccountsId } };
  }
}

export const isGoogleConfigured = (): boolean => Boolean(GOOGLE_CLIENT_ID);
export const isLinkedInConfigured = (): boolean => Boolean(LINKEDIN_CLIENT_ID);
export const getGoogleClientId = (): string => GOOGLE_CLIENT_ID;
export const getLinkedInRedirectUri = (): string => LINKEDIN_REDIRECT_URI;

let gsiLoadPromise: Promise<void> | null = null;

/** Load https://accounts.google.com/gsi/client once per page. */
export function loadGoogleIdentityServices(): Promise<void> {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (!gsiLoadPromise) {
    gsiLoadPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = GSI_SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => {
        gsiLoadPromise = null;
        reject(new Error('Google sign-in could not be loaded. Please try again.'));
      };
      document.head.appendChild(script);
    });
  }
  return gsiLoadPromise;
}

function generateState(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
}

/**
 * Store a fresh `state` and navigate to LinkedIn's authorization endpoint.
 *
 * Silent mode (default) sends `prompt=none` so LinkedIn reuses the browser's
 * existing session without asking for credentials. When no session exists
 * LinkedIn bounces back with an error — the callback page consumes the silent
 * flag and restarts non-silently so the member sees the login page once.
 */
export function startLinkedInSignIn(options?: { silent?: boolean }): void {
  const silent = options?.silent ?? true;
  const state = generateState();
  sessionStorage.setItem(LINKEDIN_STATE_KEY, state);
  if (silent) {
    sessionStorage.setItem(LINKEDIN_SILENT_AUTH_KEY, '1');
  } else {
    sessionStorage.removeItem(LINKEDIN_SILENT_AUTH_KEY);
  }
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: LINKEDIN_CLIENT_ID,
    redirect_uri: LINKEDIN_REDIRECT_URI,
    state,
    scope: LINKEDIN_SCOPES,
  });
  if (silent) params.set('prompt', 'none');
  window.location.assign(`${LINKEDIN_AUTHORIZATION_URL}?${params.toString()}`);
}

/**
 * Read-and-clear the flag marking the in-flight attempt as silent.
 * Used by the callback page to decide whether an error should trigger
 * a non-silent retry instead of surfacing a failure.
 */
export function consumeLinkedInSilentAuth(): boolean {
  const wasSilent = sessionStorage.getItem(LINKEDIN_SILENT_AUTH_KEY) === '1';
  sessionStorage.removeItem(LINKEDIN_SILENT_AUTH_KEY);
  return wasSilent;
}

/**
 * Consume and compare the `state` LinkedIn echoed back.
 * Always clears the stored value so it cannot be replayed.
 */
export function consumeLinkedInState(returnedState: string | null): boolean {
  const expected = sessionStorage.getItem(LINKEDIN_STATE_KEY);
  sessionStorage.removeItem(LINKEDIN_STATE_KEY);
  return Boolean(expected && returnedState && expected === returnedState);
}
